import {
  Announcement03,
  DotsVertical,
  LinkExternal01,
  Monitor04,
  RefreshCcw01,
  Signal01,
  Trash01,
  VideoRecorder,
} from "@untitledui/icons";
import { useState } from "react";
import { useTikTokAdsAuthUrl, useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import {
  useRemoveFromAllCampaigns,
  useRemoveFromCampaign,
  useRestoreToAllCampaigns,
  useRestoreToCampaign,
  useVideoCampaigns,
} from "@/api/video-campaigns";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { ExcludedTag, GmvMaxCampaignTag } from "@/components/assurance/badges/ad-campaign-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { success } from "@/providers/ToastProvider";

const GMV_MAX_CAMPAIGN_TYPE = "GMV_MAX";

const buildTikTokCampaignUrl = (advertiserId: string, campaignId: string) =>
  `https://ads.tiktok.com/i18n/perf/campaign?aadvid=${advertiserId}&keyword=${campaignId}`;

const buildVideosInCampaignUrl = (campaignId: string, campaignName: string) =>
  `/video-explorer?campaign=${encodeURIComponent(JSON.stringify([{ id: campaignId, label: campaignName }]))}`;

const buildAllCampaignsFilterUrl = (campaigns: { campaignId: string; campaignName: string }[]) =>
  `/video-explorer?campaign=${encodeURIComponent(JSON.stringify(campaigns.map((c) => ({ id: c.campaignId, label: c.campaignName }))))}`;

type ConfirmAction =
  | { type: "remove-single"; campaignId: string; campaignName: string | null }
  | { type: "remove-all" }
  | { type: "restore-single"; campaignId: string; campaignName: string | null }
  | { type: "restore-all" };

const isRestoreAction = (action: ConfirmAction | null): boolean =>
  action?.type === "restore-single" || action?.type === "restore-all";

const NotConnectedState = () => {
  const { data: authData } = useTikTokAdsAuthUrl();

  return (
    <div>
      <div className="inline-flex justify-start items-center gap-1.5 pb-4">
        <Announcement03 className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="text-tertiary text-sm font-medium leading-tight">Active campaigns</div>
      </div>
      <div className="relative rounded-lg border border-border-primary pb-16">
        <table className="w-full opacity-30 pointer-events-none">
          <thead className="bg-gray-50 border-b border-border-primary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Campaign Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Impressions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Orders</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">GMV</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">ROAS</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-3 text-sm text-tertiary" colSpan={5}>
                &nbsp;
              </td>
            </tr>
          </tbody>
        </table>
        <div
          className="absolute inset-0 
       bg-[linear-gradient(180deg,rgba(79,69,109,0.90)_48.16%,rgba(32,24,54,0.90)_90%)] 
       backdrop-blur-[3px] flex items-center justify-between px-8 rounded-sm min-h-50"
        >
          <div className="flex flex-col gap-1 max-w-[222px]">
            <p className="text-white text-sm font-medium">See the campaigns promoting this video</p>
            <p className="text-white/70 text-xs">Pull in the ad campaigns this video is running in automatically.</p>
          </div>
          <a
            href={authData?.authUrl ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <LinkExternal01 className="w-4 h-4" />
            Connect TikTok Ads
          </a>
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="inline-flex justify-start items-center gap-1.5">
    <Signal01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
    <div className="text-tertiary text-sm font-medium leading-tight">In 0 Active campaigns</div>
  </div>
);

interface CampaignsTableProps {
  videoId: string;
  campaigns: {
    advertiserId: string;
    campaignId: string;
    campaignName: string;
    campaignType: string;
    adId: string;
    adName: string;
    adStatus: string;
    isExcluded: boolean;
  }[];
}

type UniqueCampaign = CampaignsTableProps["campaigns"][number];

const CampaignsTable = ({ videoId, campaigns }: CampaignsTableProps) => {
  const { mutate: removeFromCampaign, isPending: isRemovingSingle } = useRemoveFromCampaign(videoId);
  const { mutate: removeFromAll, isPending: isRemovingAll } = useRemoveFromAllCampaigns(videoId);
  const { mutate: restoreToCampaign, isPending: isRestoringSingle } = useRestoreToCampaign(videoId);
  const { mutate: restoreToAll, isPending: isRestoringAll } = useRestoreToAllCampaigns(videoId);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const isRemoving = isRemovingSingle || isRemovingAll;
  const isRestoring = isRestoringSingle || isRestoringAll;
  const isBusy = isRemoving || isRestoring;

  // A campaign is treated as excluded only when every ad linking this video to it is excluded.
  const uniqueCampaigns = campaigns.reduce((acc, campaign) => {
    const existing = acc.find((c) => c.campaignId === campaign.campaignId);
    if (existing) {
      existing.isExcluded = existing.isExcluded && campaign.isExcluded;
    } else {
      acc.push({ ...campaign });
    }
    return acc;
  }, [] as UniqueCampaign[]);

  const activeCampaigns = uniqueCampaigns.filter((c) => !c.isExcluded);
  const excludedCount = uniqueCampaigns.length - activeCampaigns.length;
  const allCampaigns = uniqueCampaigns.map((c) => ({ campaignId: c.campaignId, campaignName: c.campaignName }));

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    if (confirmAction.type === "remove-single") {
      removeFromCampaign(
        { campaignId: confirmAction.campaignId },
        {
          onSuccess: () => success("Video excluded from campaign."),
          onSettled: () => setConfirmAction(null),
        },
      );
    } else if (confirmAction.type === "remove-all") {
      removeFromAll(undefined, {
        onSuccess: () => success("Video removed from all campaigns."),
        onSettled: () => setConfirmAction(null),
      });
    } else if (confirmAction.type === "restore-single") {
      restoreToCampaign(
        { campaignId: confirmAction.campaignId },
        {
          onSuccess: () => success("Exclusion cancelled. Video re-added to campaign."),
          onSettled: () => setConfirmAction(null),
        },
      );
    } else {
      restoreToAll(undefined, {
        onSuccess: () => success("Exclusions cancelled. Video re-added to all campaigns."),
        onSettled: () => setConfirmAction(null),
      });
    }
  };

  return (
    <div>
      <div className="inline-flex justify-between items-center w-full pb-4">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Announcement03 className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="text-tertiary text-sm font-medium leading-tight">
            Used in {activeCampaigns.length} Active campaign{activeCampaigns.length !== 1 ? "s" : ""}
            {excludedCount > 0 && <span className="text-utility-warning-700"> · {excludedCount} excluded</span>}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="More actions"
            >
              <DotsVertical className="w-5 h-5 text-tertiary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[260px]">
            <DropdownMenuItem
              onClick={() => setConfirmAction({ type: "remove-all" })}
              disabled={isBusy || activeCampaigns.length === 0}
              className="gap-2 cursor-pointer"
            >
              <Trash01 className="w-4 h-4 text-fg-quaternary" />
              <span className="text-secondary text-sm">Remove Video from All Campaigns</span>
            </DropdownMenuItem>
            {excludedCount > 0 && (
              <DropdownMenuItem
                onClick={() => setConfirmAction({ type: "restore-all" })}
                disabled={isBusy}
                className="gap-2 cursor-pointer"
              >
                <RefreshCcw01 className="w-4 h-4 text-fg-quaternary" />
                <span className="text-secondary text-sm">Cancel All Exclusions</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild className="gap-2 cursor-pointer">
              <a href={buildAllCampaignsFilterUrl(allCampaigns)} target="_blank" rel="noopener noreferrer">
                <Monitor04 className="w-4 h-4 text-fg-quaternary" />
                <span className="text-secondary text-sm">See Videos in All Campaigns</span>
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-lg border border-border-primary">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-border-primary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Campaign Name</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-tertiary" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {uniqueCampaigns.map((campaign) => (
              <tr key={campaign.campaignId} className={campaign.isExcluded ? "bg-gray-50/60" : undefined}>
                <td
                  className={`px-4 py-3 text-sm font-medium max-w-[180px] ${campaign.isExcluded ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <a
                      href={buildTikTokCampaignUrl(campaign.advertiserId, campaign.campaignId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors truncate"
                      title={campaign.campaignName}
                    >
                      {campaign.campaignName}
                    </a>
                    {campaign.campaignType === GMV_MAX_CAMPAIGN_TYPE && <GmvMaxCampaignTag />}
                    {campaign.isExcluded && <ExcludedTag />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                        aria-label="Campaign actions"
                      >
                        <DotsVertical className="w-4 h-4 text-tertiary" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[260px]">
                      {campaign.isExcluded ? (
                        <DropdownMenuItem
                          onClick={() =>
                            setConfirmAction({
                              type: "restore-single",
                              campaignId: campaign.campaignId,
                              campaignName: campaign.campaignName,
                            })
                          }
                          disabled={isBusy}
                          className="gap-2 cursor-pointer"
                        >
                          <RefreshCcw01 className="w-4 h-4 text-fg-quaternary" />
                          <span className="text-secondary text-sm">Cancel Exclusion</span>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() =>
                            setConfirmAction({
                              type: "remove-single",
                              campaignId: campaign.campaignId,
                              campaignName: campaign.campaignName,
                            })
                          }
                          disabled={isBusy}
                          className="gap-2 cursor-pointer"
                        >
                          <Trash01 className="w-4 h-4 text-fg-quaternary" />
                          <span className="text-secondary text-sm">Remove Video from Campaign</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild className="gap-2 cursor-pointer">
                        <a
                          href={buildVideosInCampaignUrl(campaign.campaignId, campaign.campaignName)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <VideoRecorder className="w-4 h-4 text-fg-quaternary" />
                          <span className="text-secondary text-sm">See all Videos in Campaign</span>
                        </a>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "remove-all"
                ? "Remove from all campaigns"
                : confirmAction?.type === "restore-all"
                  ? "Cancel all exclusions"
                  : confirmAction?.type === "restore-single"
                    ? "Cancel exclusion"
                    : "Remove from campaign"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "remove-all" ? (
                "This will delete all ads linking this video to its campaigns on TikTok Ads. This action cannot be undone."
              ) : confirmAction?.type === "restore-all" ? (
                "This will re-add this video to all GMV Max campaigns it was excluded from, so it can start delivering again."
              ) : confirmAction?.type === "restore-single" ? (
                <>
                  This will re-add this video to{" "}
                  <span className="font-medium">{confirmAction.campaignName ?? "this campaign"}</span>, so it can start
                  delivering again.
                </>
              ) : (
                <>
                  This will delete the ad linking this video to{" "}
                  <span className="font-medium">
                    {confirmAction?.type === "remove-single"
                      ? (confirmAction.campaignName ?? "this campaign")
                      : "this campaign"}
                  </span>{" "}
                  on TikTok Ads. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
            {isRestoreAction(confirmAction) ? (
              <AlertDialogAction onClick={handleConfirmAction} disabled={isBusy}>
                {isRestoring ? "Restoring..." : "Re-add video"}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={handleConfirmAction}
                disabled={isBusy}
                className="bg-error-solid hover:bg-error-solid/90"
              >
                {isRemoving ? "Removing..." : "Remove"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const QUARANTINE_MESSAGE =
  "A risk was found in this video, so it's been quarantined and can't run in ad campaigns while the risk is present. Once the risk policy is actioned, it will either be cleared and available to include in ad campaigns or excluded entirely.";

const QuarantinedCampaignsState = () => (
  <div className="flex flex-col gap-3">
    <div className="inline-flex justify-start items-center gap-1.5">
      <Signal01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
      <div className="text-tertiary text-sm font-medium leading-tight">Quarantined - excluded from ad campaigns</div>
    </div>
    <div className="rounded-lg border border-border-primary bg-utility-warning-50 p-4">
      <p className="text-sm text-secondary">{QUARANTINE_MESSAGE}</p>
    </div>
  </div>
);

export const CampaignsSection = ({ videoId, isQuarantined }: { videoId: string; isQuarantined?: boolean }) => {
  const { data, isLoading, error } = useVideoCampaigns(videoId);
  const { data: connectionStatus, isLoading: isLoadingConnection } = useTikTokAdsConnectionStatus();

  if (isQuarantined) {
    return <QuarantinedCampaignsState />;
  }

  if (isLoading || isLoadingConnection) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-error-primary">Failed to load campaign data</p>
      </div>
    );
  }

  const isConnected = connectionStatus?.connected === true;
  const campaigns = data?.campaigns ?? [];

  if (!isConnected) {
    return <NotConnectedState />;
  }

  if (campaigns.length === 0) {
    return <EmptyState />;
  }

  return <CampaignsTable videoId={videoId} campaigns={campaigns} />;
};
